import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Layout from '../../elements/Layout';
import moment from 'moment';
import { fetchPlots, fetchProjectsByType } from '../../utils/api/api';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';

const PlotsReports = () => {
    const [filters, setFilters] = useState({
        status: '',
        project_id: '',
    });
    const [projects, setProjects] = useState([]);
    const [plots, setPlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const loadProjects = async () => {
            try {
                const response = await fetchProjectsByType({ page: 0, pageSize: 0 });
                const formattedProjects = response.projects.map(project => ({
                    value: project.id,
                    label: project.title,
                }));
                setProjects(formattedProjects);
            } catch (error) {
                console.error('Failed to fetch projects.', error);
            }
        };
        loadProjects();
    }, []);

    const fetchPlotsData = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchPlots(filters);
            const mappedPlots = data.properties.map(plot => ({
                id: plot.id,
                property_number: plot.property_number,
                title_number: plot.title_number,
                status: plot.status,
                createdAt: plot.createdAt,
                project_name: plot.project?.title || 'N/A',
                project_code: plot.project?.code || 'N/A',
            }));
            setPlots(mappedPlots);
        } catch (err) {
            setError('Failed to fetch plots. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlotsData();
    }, [filters]);

    const handleSelectChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value,
        }));
    };
    const handleExportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(plots);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Plots');
        XLSX.writeFile(workbook, 'plots_report.xlsx');
    };

    return (
        <Layout>
            <div className="min-h-screen">
                <div className="border border-gray-200 p-2 flex items-center w-full justify-between rounded-md mb-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <select
                                name="status"
                                value={filters.status}
                                onChange={(e) => handleSelectChange('status', e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                            >
                                <option value="">All</option>
                                <option value="available">Available</option>
                                <option value="under_contract">Under Contract</option>
                                <option value="sold">Sold</option>
                                <option value="preserved">Preserved</option>
                                <option value="booked">Booked</option>
                                <option value="reserved">Reserved</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Project</label>
                            <Select
                                options={projects}
                                onChange={(selected) => handleSelectChange('project_id', selected ? selected.value : '')}
                                isClearable
                                placeholder="Select project"
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <button
                        onClick={handleExportToExcel}
                        className="bg-primary text-white px-6 text-[13px] py-1 rounded-md shadow hover:bg-primary-dark"
                    >
                        Export to Excel
                    </button>
                </div>

                <div className="bg-white border border-gray-200 rounded-md">
                    {error && <p className="text-red-600">{error}</p>}
                    {!loading && plots.length === 0 && <p className="text-gray-500">No plots found.</p>}
                    {loading && <p className="text-gray-500">Loading...</p>}
                    {plots.length > 0 && (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full table-auto border-collapse rounded-md">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-700">
                                            <th className="border-b text-[18px] font-semibold border-gray-200 text-start px-4 py-2">#</th>
                                            <th className="border-b text-[18px] font-semibold border-gray-200 text-start px-4 py-2">Property Number</th>
                                            <th className="border-b text-[18px] font-semibold border-gray-200 text-start px-4 py-2">Status</th>
                                            <th className="border-b text-[18px] font-semibold border-gray-200 text-start px-4 py-2">Project</th>
                                            <th className="border-b text-[18px] font-semibold border-gray-200 text-start px-4 py-2">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {plots.map((plot, index) => (
                                            <tr key={plot.id} onClick={() => navigate(`/dashboard/properties/${plot.id}`)} className="cursor-pointer hover:bg-gray-50 text-black">
                                                <td className="border-b text-[13px] text-black whitespace-nowrap border-gray-200 px-4 py-2">{index + 1}</td>
                                                <td className="border-b text-[13px] text-black whitespace-nowrap border-gray-200 px-4 py-2">{plot.property_number}</td>
                                                <td className="border-b text-[13px] text-black whitespace-nowrap border-gray-200 px-4 py-2 capitalize">{plot.status.replace('_', ' ')}</td>
                                                <td className="border-b text-[13px] text-black whitespace-nowrap border-gray-200 px-4 py-2 uppercase">{plot.project_name}</td>
                                                <td className="border-b text-[13px] text-black whitespace-nowrap border-gray-200 px-4 py-2">
                                                    {moment(plot.createdAt).format('MMM DD, YYYY, HH:MM A')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default PlotsReports;
